// Vendedores — Portal Admin VentasLuga. Banda de periodo y explorer filtros · tabla · ficha.
// El vendedor va en `?vendedor=` y el periodo en `?periodo=`.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenFrame, ScreenFrameNotice } from '@/shared/patterns/ScreenFrame';
import {
    DEFAULT_REPORT_PERIOD,
    PeriodBand,
    parseReportPeriod,
    type ReportPeriod,
} from '@/shared/patterns/PeriodBand';
import { ReconciliationEntityEnum } from '@fiado/type-kit/bin/retailIntegration/enums/ReconciliationEntityEnum';
import { usePermissions } from '@/kit';
import { useAuth } from '@/hooks/useAuth';
import { resolveOwnRetailerId } from '@/domains/retail/ventas/lib/retailerScope';
import { useRosterRetailer } from '../lib/useRoster';
import { useSellerPeriodSales, useSellersTable, useSellerTrendSales } from './api/useSellersTable';
import { SellersFiltersPanel } from './components/SellersFiltersPanel';
import { NewSellerButton } from './components/NewSellerDrawer';
import { ReconcileButton } from '../reconciliation/components/ReconcileButton';
import { RetailerIntegrationLock } from '../integration/components/IntegrationLock';
import { SellersTable } from './components/SellersTable';
import {
    SellerDetailPanel,
    type SalesBlock,
    type SellerDetailTab,
} from './components/SellerDetailPanel';
import {
    buildContext,
    DEFAULT_SELLER_SORT,
    EMPTY_SELLERS_FILTERS,
    filterSellers,
    sortSellers,
    storeOptions,
    toggleSort,
    type SellerSort,
    type SellersFilters,
} from './lib/sellersTable';

/** La tabla scrollea dentro de su columna; el detalle conserva un ancho legible. */
const EXPLORER_SPLIT = { list: 'minmax(0,3fr)', detail: 'minmax(360px,2fr)' };

const RECONCILE_TARGET = { entity: ReconciliationEntityEnum.USERS } as const;

export const LAKE_PENDING_NOTICE =
    'El historial completo llegará con el data lake; por ahora «Todo» suma lo que alcanza a leer de las ventas.';
export const LAKE_TRUNCATED_NOTICE =
    'Esta vez se llegó al tope de lectura: las cifras son parciales.';

/** El permiso del listado de ventas, que no es el de esta pantalla. */
const SALES_PERMISSION = 'retail.sale.read.all';

function asError(error: unknown): Error | null {
    if (!error) return null;
    if (error instanceof Error) return error;
    return new Error(typeof error === 'string' ? error : 'Error desconocido');
}

/** Con la cadena integrada, las escrituras del padrón quedan bajo candado. */
export function VendedoresPage() {
    const { retailerId } = useRosterRetailer();
    return (
        <RetailerIntegrationLock retailerId={retailerId}>
            <VendedoresScreen />
        </RetailerIntegrationLock>
    );
}

function VendedoresScreen() {
    const navigate = useNavigate();
    const { vendedor, periodo } = useSearch({ from: '/_backoffice/retail-admin/$section' });
    const period = parseReportPeriod(periodo);

    const [filters, setFilters] = useState<SellersFilters>(EMPTY_SELLERS_FILTERS);
    const [sort, setSort] = useState<SellerSort>(DEFAULT_SELLER_SORT);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const [detailTab, setDetailTab] = useState<SellerDetailTab>('performance');

    const { roleAssignments } = useAuth();
    const fixedRetailerId = resolveOwnRetailerId(roleAssignments);
    const { hasPermission } = usePermissions();
    const { retailerId: rosterRetailerId } = useRosterRetailer();
    const salesBlock: SalesBlock = !hasPermission(SALES_PERMISSION)
        ? 'noPermission'
        : fixedRetailerId === null
          ? 'noRetailer'
          : 'ok';

    const list = useSellersTable(period);
    const salesRetailerId = salesBlock === 'ok' ? fixedRetailerId : null;
    const sales = useSellerPeriodSales(vendedor ?? null, salesRetailerId, period);
    const trend = useSellerTrendSales(
        vendedor ?? null,
        salesRetailerId,
        detailTab === 'performance',
    );

    const rows = useMemo(() => list.data?.items ?? [], [list.data]);
    const context = useMemo(() => buildContext(rows), [rows]);
    const visibleRows = useMemo(
        () => sortSellers(filterSellers(rows, filters, context), sort),
        [rows, filters, context, sort],
    );
    const stores = useMemo(() => storeOptions(rows), [rows]);
    const selectedRow = rows.find((r) => r.sellerId === vendedor);
    const listError = asError(list.error);
    const rbacAvailable = list.data?.rbacAvailable ?? false;
    const showLakeNotice = period === 'all' && list.data?.lake === 'DISABLED';

    const selectSeller = (sellerId: string | null) => {
        void navigate({
            to: '/retail-admin/$section',
            params: { section: 'vendedores' },
            search: (prev) => ({ ...prev, vendedor: sellerId ?? undefined }),
            replace: true,
        });
    };

    const changePeriod = (next: ReportPeriod) => {
        void navigate({
            to: '/retail-admin/$section',
            params: { section: 'vendedores' },
            search: (prev) => ({
                ...prev,
                periodo: next === DEFAULT_REPORT_PERIOD ? undefined : next,
            }),
            replace: true,
        });
    };

    // Autoselecciona el primero solo si no hay selección; un `?vendedor=` fuera de la lista se respeta.
    useEffect(() => {
        if (!vendedor && visibleRows.length > 0) {
            selectSeller(visibleRows[0].sellerId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- selectSeller se re-crea en cada render
    }, [vendedor, visibleRows]);

    return (
        <ScreenFrame>
            <PeriodBand
                period={period}
                onPeriodChange={changePeriod}
                truncated={list.data?.truncated ?? false}
                goalNote={rows.some((r) => r.goalCents !== null)}
            />
            {showLakeNotice ? (
                <ScreenFrameNotice role="status">
                    {LAKE_PENDING_NOTICE}
                    {list.data?.truncated ? ` ${LAKE_TRUNCATED_NOTICE}` : null}
                </ScreenFrameNotice>
            ) : null}
            <MasterDetailExplorer
                filtersCollapsed={filtersCollapsed}
                split={EXPLORER_SPLIT}
                filters={
                    <SellersFiltersPanel
                        rows={rows}
                        filters={filters}
                        onChange={setFilters}
                        stores={stores}
                        collapsed={filtersCollapsed}
                        rbacAvailable={rbacAvailable}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed((v) => !v);
                        }}
                    />
                }
                list={
                    <SellersTable
                        rows={visibleRows}
                        allRows={rows}
                        context={context}
                        sort={sort}
                        onSortToggle={(key) => {
                            setSort((current) => toggleSort(current, key));
                        }}
                        pill={filters.pill}
                        onPillChange={(pill) => {
                            setFilters({ ...filters, pill });
                        }}
                        selectedId={vendedor ?? null}
                        onSelect={selectSeller}
                        isLoading={list.isPending}
                        error={listError}
                        onRetry={list.refetch}
                        period={period}
                        rbacAvailable={rbacAvailable}
                        actions={
                            <>
                                <ReconcileButton
                                    retailerId={rosterRetailerId}
                                    target={RECONCILE_TARGET}
                                    onComplete={list.refetch}
                                />
                                <NewSellerButton retailerId={rosterRetailerId} />
                            </>
                        }
                    />
                }
                detail={
                    <SellerDetailPanel
                        retailerId={rosterRetailerId}
                        row={selectedRow}
                        rows={rows}
                        context={context}
                        hasSelection={Boolean(vendedor)}
                        isLoading={list.isPending}
                        error={listError}
                        onRetry={list.refetch}
                        tab={detailTab}
                        onTabChange={setDetailTab}
                        sales={{
                            block: salesBlock,
                            items: sales.data?.items,
                            truncated: sales.data?.truncated ?? false,
                            isLoading: sales.isPending && salesBlock === 'ok',
                            error: asError(sales.error),
                        }}
                        trend={{
                            block: salesBlock,
                            items: trend.data?.items,
                            truncated: trend.data?.truncated ?? false,
                            isLoading: trend.isPending && salesBlock === 'ok',
                            error: asError(trend.error),
                        }}
                        period={period}
                        commission={{
                            available: list.data?.commissionAvailable ?? false,
                            period: list.data?.commissionPeriod ?? null,
                        }}
                    />
                }
            />
        </ScreenFrame>
    );
}
